/*
 *   File : upsert-faq.js
 *   Author : https://evoqins.com
 *   Description : upsert faq component
 *   Integrations : NA
 *   Version : 1.0.0
 */
import React, { useEffect, useState } from "react";
import {
  CheckboxSelect,
  CustomTextArea,
  Input,
  TextEditor,
} from "../../../../Components/FormElements";
import { PrimaryButton } from "../../../../Components/Buttons";
import { SuccessModal } from "../../../../Components/Modals";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  setNavLink,
  setPageTitle,
  setSearchEnabled,
} from "../../../../Redux/Ui/ui.slice";
import { APIService } from "../../../../Service";
import { toast } from "react-toastify";

export default function UpsertFaq() {
  const { id } = useParams();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  useEffect(() => {
  _getFilterData();

  if (id) dispatch(setPageTitle(["Manage Content", "Edit FAQ"]));
  else dispatch(setPageTitle(["Manage Content", "Add FAQ"]));

  dispatch(setNavLink(["/manage-content/faq"]));
  dispatch(setSearchEnabled(false));
}, [id]);


  /* ----------------------------------------
      STATES
  ---------------------------------------- */
  const [question, setQuestion] = useState("");
  const [questionError, setQuestionError] = useState("");

  const [answer, setAnswer] = useState("");
  const [answerError, setAnswerError] = useState("");

  const [loading, setLoading] = useState(false);
  const [successModal, setSuccessModal] = useState(false);

  const [filterData, setFilterData] = useState([]);
  const [category, setCategory] = useState([]);
  const [categoryError, setCategoryError] = useState("");

  useEffect(() => {
  if (!id || filterData.length === 0) return;

  _getCurQuestion();
}, [id, filterData]);

  /* ----------------------------------------
      VALIDATION
  ---------------------------------------- */
  const validate = () => {
    let isValid = true;

    if (!question.trim()) {
      setQuestionError("Question is required");
      isValid = false;
    }

    if (!answer.trim()) {
      setAnswerError("Answer is required");
      isValid = false;
    }

    if (category.length === 0) {
      setCategoryError("Category is required");
      isValid = false;
    }

    return isValid;
  };
  /* ----------------------------------------
      SUBMIT HANDLER
  ---------------------------------------- */
  const _submitForm = () => {
    if (!validate()) return;

    setLoading(true);

    const categoryId = Array.isArray(category)
      ? category?.[0]?.value
      : category?.value;

    const payload = {
      type: id ? 2 : 1,
      category_id: categoryId,
      question_id: id || null,
      question: question?.trim(),
      answer: answer?.trim(),
    };

    APIService(true, "content/upsert-faq", payload)
      .then((res) => {
        if (res?.status_code === 200) {
          toast.success(res?.message);
          setSuccessModal(true);
        } else {
          toast.error(res?.message || "Something went wrong");
        }
      })
      .catch((error) => {
        console.error("Error submitting FAQ:", error);
        toast.error(error?.message || "Something went wrong");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  //get filter data
  const _getFilterData = async () => {
    const response = await APIService(false, "content/faq-filter_types");
    if (response?.status_code !== 200) {
      toast.error(response?.message);
      return;
    }
    setFilterData(response.data);
  };

  //get curretn question
  const _getCurQuestion = async () => {
    try {
      const response = await APIService(true, "content/view-faq", {
        faq_id: id,
      });

      if (response?.status_code !== 200) {
        toast.error(response?.message);
        return;
      }

      const faq = response?.data?.[0];
      if (!faq) return;

      // Set question & answer
      setQuestion(faq.question || "");
      setAnswer(faq.answer || "");
      if (faq.category_name && filterData.length > 0) {
        const matchedCategory = filterData.find(
          (item) => item.label === faq.category_name,
        );

        if (matchedCategory) {
          setCategory([matchedCategory]); // because CheckboxSelect expects array
        }
      }
    } catch (error) {
      console.error("Error fetching FAQ:", error);
      toast.error("Failed to load FAQ");
    }
  };

  /* ----------------------------------------
      RENDER
  ---------------------------------------- */
  return (
    <div className="row">
      <div className="col-12">
        <div className="d-flex flex-column e-glass-card rounded-24 p-24">
          <div className="row">
            <div className="col-6">
              {/* QUESTION */}
              <Input
                id="question"
                type="text"
                label="Question"
                placeholder="Add a Question"
                require={true}
                value={question}
                error={questionError}
                className="w-100 px-2 py-10"
                handleChange={(val) => {
                  setQuestion(val);
                  setQuestionError("");
                }}
                autoFocus
              />
              <div className="d-flex flex-column pt-1 mb-3">
                <p className="mb-0 fw-400 fs-14 pb-1">
                  Category <span className="text-cg-red">*</span>
                </p>

                <CheckboxSelect
                  options={filterData}
                  value={category}
                  onChange={(val) => {
                    setCategory(val);
                    setCategoryError("");
                  }}
                  placeholder="Select category"
                  type={1}
                  error={categoryError}
                />
              </div>

              {/* ANSWER */}
              <div className="d-flex flex-column">
                <p className="fw-400 fs-14 mb-0">
                  Answer
                  <span className="text-cg-red">*</span>
                </p>
                <div
                  className={`d-flex flex-column e-glass-bg shadow-none ${answerError ? "border-cg-red" : "border-gainsboro "} rounded-16 mt-1 flex-grow-1`}
                >
                  <TextEditor
                    value={answer}
                    onChange={(v) => {
                      setAnswer(v);
                      setAnswerError("");
                    }}
                    error={answerError}
                    placeholder="Write Answer..."
                    className={""}
                  />
                </div>
                {answerError && (
                  <span className="text-cg-red fs-12">{answerError}</span>
                )}
              </div>

              {/* BUTTONS */}
              <div className="row row-cols-2 pt-24">
                <div className="col">
                  <PrimaryButton
                    name="Cancel"
                    ClassName="rounded-pill bg-transparent border-oxford-blue py-3 text-oxford-blue w-100 fs-16 fw-500"
                    onPress={() => navigate("/manage-content/faq")}
                  />
                </div>

                <div className="col">
                  <PrimaryButton
                    name={id ? "Edit" : "Add"}
                    ClassName="rounded-pill bg-oxford-blue py-3 text-white w-100 fs-16 fw-500"
                    onPress={_submitForm}
                    loading={loading}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SUCCESS MODAL */}
      <SuccessModal
        title="Successful"
        message="FAQ saved successfully"
        open={successModal}
        onConfirm={() => {
          setSuccessModal(false);
          navigate("/manage-content/faq");
        }}
      />
    </div>
  );
}
